import FindConversationResponseMessage from 'owa-service/lib/contract/FindConversationResponseMessage';
import getConversationSortByProperty from 'owa-mail-triage-table-utils/lib/getConversationSortByProperty';
import onLoadInitialRowsSucceeded from '../onLoadInitialRowsSucceeded';
import RequestOptions from 'owa-service/lib/RequestOptions';
import { action } from 'satcheljs/lib/legacy';
import {
    getBaseFolderId,
    getBaseServerFolderId,
    TableView,
    MailFolderTableQuery,
    MailSortHelper,
} from 'owa-mail-list-store';
import { getMailboxInfoFromTableQuery } from 'owa-mail-mailboxinfo';
import { INITIAL_LOAD_ROW_COUNT } from 'owa-mail-triage-table-utils';
import { isFeatureEnabled } from 'owa-feature-flags';
import { OnInitialTableLoadComplete } from 'owa-mail-loading-action-types';
import { ServiceFetchError } from 'owa-service/lib/ServiceFetchError';
import {
    findConversationWithStartIndex,
    getFindConversationsRestrictions,
} from 'owa-mail-find-rows';
import getHashtagsToExclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToExclude';
import getHashtagsToInclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToInclude';

/**
 * Load the initial conversations from the server
 * @param tableView to load conversations in
 * @param OnInitialTableLoadComplete is a callback that is called when we receive the response
 * The callback is handled by table loading
 * @param isTablePrefetched indicates if the table is prefetched
 * @return a promise that resolves when the load table from server has completed
 */
export default action('loadInitialConversationsFromServer')(
    function loadInitialConversationsFromServer(
        tableView: TableView,
        isTablePrefetched: boolean,
        onInitialTableLoadComplete: OnInitialTableLoadComplete
    ): Promise<void> {
        const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;
        const isFilter = mailTableQuery.viewFilter && mailTableQuery.viewFilter !== 'All';
        const { sortColumn, sortDirection } = MailSortHelper.getDefaultSortBy();
        const isSort =
            mailTableQuery.sortBy &&
            (mailTableQuery.sortBy.sortColumn !== sortColumn ||
                mailTableQuery.sortBy.sortDirection !== sortDirection);

        let requestOptions = getMailboxInfoFromTableQuery(mailTableQuery).requestOptions;
        if (requestOptions) {
            requestOptions.datapoint = {
                customData: {
                    scenarioType: mailTableQuery.scenarioType,
                    isPrefetch: isTablePrefetched,
                    isFilter: isFilter,
                    isSort: isSort,
                    isRoamingFavoritesEnabled: isFeatureEnabled('tri-favorites-roaming'), // https://msfast.visualstudio.com/FAST/_workitems/edit/260700: Remove this line once flight is 100% WW
                },
            };
        }

        const findConversationRestrictions = getFindConversationsRestrictions(tableView);

        const findConversationResponse = findConversationWithStartIndex(
            getBaseFolderId(mailTableQuery),
            0 /* startIndex */,
            INITIAL_LOAD_ROW_COUNT,
            mailTableQuery.viewFilter,
            getConversationSortByProperty(mailTableQuery),
            mailTableQuery.focusedViewFilter,
            mailTableQuery.requestShapeName,
            mailTableQuery.shouldTryToUseSessionDataResponse,
            requestOptions,
            mailTableQuery.categoryName,
            getHashtagsToInclude(mailTableQuery.inboxClassificationFilter),
            getHashtagsToExclude(mailTableQuery.inboxClassificationFilter),
            getBaseServerFolderId(tableView, true /* returnNullIfSameAsFolderId */),
            findConversationRestrictions /* restriction required for static folders */,
            findConversationRestrictions /* refinerRestriction  required for search folders */
        );

        return processFindConversationResponse(
            findConversationResponse,
            tableView,
            onInitialTableLoadComplete,
            isTablePrefetched
        );
    }
);

function processFindConversationResponse(
    findConversation: Promise<any>,
    tableView: TableView,
    onInitialTableLoadComplete: OnInitialTableLoadComplete,
    isTablePrefetched: boolean
): Promise<void> {
    return findConversation.then(
        (response: {
            findConversationResponseMessage: FindConversationResponseMessage;
            responseInit: RequestOptions;
            url: string;
        }) => {
            let promiseToReturn = Promise.resolve();
            const responseMessage = response.findConversationResponseMessage;

            const isSuccessResponseClass = responseMessage.ResponseClass == 'Success';
            if (isSuccessResponseClass) {
                onLoadInitialRowsSucceeded(
                    tableView,
                    responseMessage.Conversations,
                    responseMessage.TotalConversationsInView,
                    responseMessage.SearchFolderId,
                    responseMessage.FolderId
                );
            } else {
                const error = new Error(
                    `ResponseCode=${responseMessage.ResponseCode}`
                ) as ServiceFetchError;

                error.fetchErrorType = 'ServerFailure';
                error.response = new Response(undefined, <ResponseInit>response.responseInit);
                promiseToReturn = Promise.reject(error);
            }

            onInitialTableLoadComplete(
                tableView,
                isSuccessResponseClass,
                responseMessage.ResponseCode,
                isTablePrefetched
            );

            return promiseToReturn;
        },
        error => {
            // Server returned non-200 response
            const responseCode = error?.response?.status || '';
            onInitialTableLoadComplete(tableView, false, responseCode, isTablePrefetched);
            return Promise.reject(error);
        }
    );
}
